// ContentBank.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Image,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Linking } from 'react-native';

const PAGE_SIZE = 10;

const ContentBank = ({ navigation }) => {
  // Content
  const [items, setItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  // Per-media metadata (aspect ratio + loaded state)
  // { [url]: { ratio: number, loaded: boolean } }
  const [mediaMeta, setMediaMeta] = useState({});

  // NEW: session + auth state for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  const { width } = useWindowDimensions();

  const columnCount = useMemo(() => {
    if (width >= 1100) return 4;
    if (width >= 820) return 3;
    if (width >= 520) return 2;
    return 1;
  }, [width]);

  const normalizeUrl = (u) => String(u || '').trim();

  const getMediaType = useCallback((url) => {
    const u = normalizeUrl(url);
    const lower = u.toLowerCase();
    if (!u) return 'link';

    // Treat Instagram as external link (can’t be directly rendered/downloaded)
    if (lower.includes('instagram.com')) return 'link';

    const clean = lower.split('?')[0].split('#')[0];

    if (/\.(mp4|mov|webm|m4v|ogg)$/i.test(clean)) return 'video';
    if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(clean)) return 'image';

    // Unknown URL type -> external link card (safe)
    return 'link';
  }, []);

  // ---- HELPERS ----
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const openUrl = (url) => {
    if (!url) return;
    Linking.openURL(url);
  };

  const downloadMedia = (url) => {
    if (!url) return;

    // Only try download for direct files (image/video)
    const type = getMediaType(url);
    if (type === 'link') {
      openUrl(url);
      return;
    }

    // Web: force download (best-effort)
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      } catch (e) {
        // fallback below
      }
    }

    // Native fallback
    openUrl(url);
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  // Session
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => setUserData(data))
      .catch((error) => console.error('Error fetching user data:', error));
  }, []);

  // Subscription/auth state for sidenav
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  const fetchBatch = useCallback(async (nextOffset, isLoadMore) => {
    setErrorMsg('');
    if (isLoadMore) setLoadingMore(true);
    else setInitialLoading(true);

    try {
      const url = `https://collabbayadmin.com/APIControllers/FetchBrandContentBank.php?limit=${PAGE_SIZE}&offset=${nextOffset}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to load content bank.');
      }

      const nextItems = Array.isArray(data.items) ? data.items : [];

      setItems((prev) =>
        nextOffset === 0 ? nextItems : [...prev, ...nextItems]
      );

      const nextOffsetCalc =
        typeof data.next_offset === 'number'
          ? data.next_offset
          : nextOffset + nextItems.length;

      setOffset(nextOffsetCalc);

      const more =
        typeof data.has_more === 'boolean'
          ? data.has_more
          : nextItems.length === PAGE_SIZE;

      setHasMore(more);
    } catch (e) {
      setErrorMsg(e.message || 'Something went wrong.');
    } finally {
      if (isLoadMore) setLoadingMore(false);
      else setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatch(0, false);
  }, [fetchBatch]);

  // Preload aspect ratios for images only (prevents crashes on IG links)
  useEffect(() => {
    if (!items || items.length === 0) return;

    items.forEach((row) => {
      const url = normalizeUrl(row?.Image);
      if (!url) return;

      const type = getMediaType(url);
      if (type !== 'image') return;

      if (mediaMeta[url]?.ratio) return;

      Image.getSize(
        url,
        (w, h) => {
          const ratio = w && h ? w / h : 1;
          setMediaMeta((prev) => ({
            ...prev,
            [url]: { ...(prev[url] || {}), ratio: ratio || 1 },
          }));
        },
        () => {
          setMediaMeta((prev) => ({
            ...prev,
            [url]: { ...(prev[url] || {}), ratio: 1 },
          }));
        }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const markLoaded = (url) => {
    if (!url) return;
    setMediaMeta((prev) => ({
      ...prev,
      [url]: { ...(prev[url] || {}), loaded: true },
    }));
  };

  // Masonry columns (balanced by estimated heights)
  const columns = useMemo(() => {
    const cols = Array.from({ length: columnCount }, () => ({
      items: [],
      h: 0,
    }));

    const clamp = (num, min, max) => Math.max(min, Math.min(max, num));

    items.forEach((row) => {
      const url = normalizeUrl(row?.Image);
      if (!url) return;

      const type = getMediaType(url);
      const ratio = mediaMeta[url]?.ratio || 1;

      let est = 0.75;
      if (type === 'image') est = 1 / clamp(ratio, 0.6, 2.0);
      if (type === 'video') est = 0.8;
      if (type === 'link') est = 0.55;

      let minIdx = 0;
      for (let i = 1; i < cols.length; i++) {
        if (cols[i].h < cols[minIdx].h) minIdx = i;
      }

      cols[minIdx].items.push({
        ...row,
        _url: url,
        _type: type,
      });
      cols[minIdx].h += est;
    });

    return cols.map((c) => c.items);
  }, [items, columnCount, mediaMeta, getMediaType]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchBatch(offset, true);
  };

  // ⏳ LOADING STATE — shell but no sidenav (matches your pattern)
  if (initialLoading) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={[styles.loadingCenter, { flex: 1 }]}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Loading content bank…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // MAIN RENDER — with sidenav + content
  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDE NAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BusinessAccountPage')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/subscribe')
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() => Linking.openURL('mailto:info@collab-bay.com')}
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.pageInner}>
                {/* TOP BAR */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBackPress}
                  >
                    <Ionicons name="arrow-back" size={20} color="black" />
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>

                  <View style={styles.bannerRight}>
                    <Text style={styles.smallWelcome}>
                      {userData.Username
                        ? `Hi, ${userData.Username}`
                        : 'Brand account'}
                    </Text>
                  </View>
                </View>

                <View style={styles.bannerCard}>
                  <View>
                    <Text style={styles.bannerTitle}>My brand content bank</Text>
                    <Text style={styles.bannerSubtitle}>
                      Boom. Each collab is more than just promotion- it's an addition to your content portolio- build it, recycle it, and edit it as you wish. All creator photos & videos — ready to download.
                    </Text>
                  </View>
                  <Ionicons name="images-outline" size={28} color="black" />
                </View>

                {/* Header row */}
                <View style={styles.galleryHeaderRow}>
                  <Text style={styles.galleryTitle}>Gallery</Text>
                  <Text style={styles.galleryMeta}>
                    {items.length} item{items.length === 1 ? '' : 's'}
                  </Text>
                </View>

                {/* Error */}
                {errorMsg ? (
                  <View style={styles.errorCard}>
                    <View style={styles.errorRow}>
                      <Ionicons name="alert-circle-outline" size={18} color="#111" />
                      <Text style={styles.errorTitle}>Couldn’t load content</Text>
                    </View>
                    <Text style={styles.errorBody}>{errorMsg}</Text>
                    <TouchableOpacity
                      style={styles.retryBtn}
                      onPress={() => fetchBatch(0, false)}
                      activeOpacity={0.9}
                    >
                      <Text style={styles.retryBtnText}>Retry</Text>
                      <Ionicons
                        name="refresh-outline"
                        size={16}
                        color="#fff"
                        style={{ marginLeft: 6 }}
                      />
                    </TouchableOpacity>
                  </View>
                ) : null}

                {/* Empty */}
                {!errorMsg && items.length === 0 ? (
                  <View style={styles.emptyCard}>
                    <Ionicons name="cloud-outline" size={22} color="#111" />
                    <Text style={styles.emptyTitle}>No content yet</Text>
                    <Text style={styles.emptySubtitle}>
                      Once creators upload content, it will appear here for instant download.
                    </Text>
                  </View>
                ) : null}

                {/* Masonry */}
                {items.length > 0 && (
                  <View style={styles.masonryRow}>
                    {columns.map((colItems, colIdx) => (
                      <View key={`col-${colIdx}`} style={styles.masonryCol}>
                        {colItems.map((row, idx) => {
                          const url = row._url;
                          const type = row._type;
                          const loaded = !!mediaMeta[url]?.loaded;
                          const ratio = mediaMeta[url]?.ratio || 1;

                          const isExternal = type === 'link';

                          return (
                            <View key={`${url}-${idx}`} style={styles.mediaCard}>
                              <View style={styles.mediaTop}>
                                {!loaded && !isExternal && (
                                  <View style={styles.mediaPlaceholder}>
                                    <ActivityIndicator size="small" color="#111" />
                                  </View>
                                )}

                                {type === 'video' ? (
                                  Platform.OS === 'web' ? (
                                    <video
                                      src={url}
                                      controls
                                      playsInline
                                      onLoadedData={() => markLoaded(url)}
                                      style={{
                                        width: '100%',
                                        borderRadius: 14,
                                        display: 'block',
                                        backgroundColor: '#000',
                                      }}
                                    />
                                  ) : (
                                    <TouchableOpacity
                                      activeOpacity={0.9}
                                      onPress={() => openUrl(url)}
                                      style={styles.nativeVideoFallback}
                                    >
                                      <Ionicons
                                        name="play-circle-outline"
                                        size={44}
                                        color="#fff"
                                      />
                                      <Text style={styles.nativeVideoFallbackText}>
                                        Open video
                                      </Text>
                                    </TouchableOpacity>
                                  )
                                ) : type === 'image' ? (
                                  <Image
                                    source={{ uri: url }}
                                    resizeMode="cover"
                                    onLoadEnd={() => markLoaded(url)}
                                    style={[
                                      styles.mediaImage,
                                      { aspectRatio: Math.max(0.7, Math.min(1.8, ratio || 1)) },
                                    ]}
                                  />
                                ) : (
                                  // External link (Instagram etc.)
                                  <TouchableOpacity
                                    activeOpacity={0.92}
                                    onPress={() => openUrl(url)}
                                    style={styles.externalCard}
                                  >
                                    <View style={styles.externalTopRow}>
                                      <Ionicons name="link-outline" size={18} color="#000" />
                                      <Text style={styles.externalTitle} numberOfLines={1}>
                                        External link
                                      </Text>
                                    </View>

                                    <View style={styles.externalMid}>
                                      <Ionicons
                                        name="logo-instagram"
                                        size={34}
                                        color="#111"
                                      />
                                      <Text style={styles.externalSub}>
                                        Open on Instagram / browser
                                      </Text>
                                    </View>

                                    <Text style={styles.externalUrl} numberOfLines={2}>
                                      {url}
                                    </Text>
                                  </TouchableOpacity>
                                )}

                                <View style={styles.typePill}>
                                  <Text style={styles.typePillText}>
                                    {type === 'video'
                                      ? 'VIDEO'
                                      : type === 'image'
                                      ? 'IMAGE'
                                      : 'LINK'}
                                  </Text>
                                </View>
                              </View>

                              <View style={styles.mediaFooter}>
                                <TouchableOpacity
                                  style={[
                                    styles.downloadBtn,
                                    isExternal && styles.downloadBtnDisabled,
                                  ]}
                                  onPress={() => downloadMedia(url)}
                                  activeOpacity={0.9}
                                >
                                  <Ionicons
                                    name="download-outline"
                                    size={16}
                                    color={isExternal ? '#777' : '#000'}
                                  />
                                  <Text
                                    style={[
                                      styles.downloadBtnText,
                                      isExternal && styles.downloadBtnTextDisabled,
                                    ]}
                                  >
                                    {isExternal ? 'Download N/A' : 'Download'}
                                  </Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                  style={styles.openBtn}
                                  onPress={() => openUrl(url)}
                                  activeOpacity={0.9}
                                >
                                  <Ionicons name="open-outline" size={16} color="#111" />
                                  <Text style={styles.openBtnText}>Open</Text>
                                </TouchableOpacity>
                              </View>
                            </View>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                )}

                {/* Load more */}
                {items.length > 0 && (
                  <View style={styles.loadMoreWrap}>
                    {hasMore ? (
                      <TouchableOpacity
                        style={styles.loadMoreBtn}
                        onPress={handleLoadMore}
                        activeOpacity={0.9}
                        disabled={loadingMore}
                      >
                        {loadingMore ? (
                          <>
                            <ActivityIndicator size="small" color="#000" />
                            <Text style={styles.loadMoreText}>Loading…</Text>
                          </>
                        ) : (
                          <>
                            <Text style={styles.loadMoreText}>Load more</Text>
                            <Ionicons name="chevron-down" size={18} color="#000" />
                          </>
                        )}
                      </TouchableOpacity>
                    ) : (
                      <Text style={styles.endText}>
                        You’ve reached the end of the content bank.
                      </Text>
                    )}
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  // SHELL
  root: { flex: 1, backgroundColor: '#f3f4f6' },
  safeArea: { flex: 1 },
  shellRow: { flex: 1, flexDirection: 'row' },

  // MAIN AREA
  mainArea: { flex: 1, backgroundColor: '#f3f3f3' },
  scrollContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'center',
  },

  // LOADING
  loadingCenter: { justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 8, color: '#555', fontSize: 14 },

  // PAGE INNER
  pageInner: { width: '100%', maxWidth: 1100, alignSelf: 'center' },

  // HEADER / BANNER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: { marginLeft: 6, fontSize: 13, color: '#333', fontWeight: '500' },
  bannerRight: { alignItems: 'flex-end' },
  smallWelcome: { fontSize: 12, color: '#555' },
  bannerCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerTitle: { fontSize: 18, fontWeight: '700', color: '#111', marginBottom: 4 },
  bannerSubtitle: { fontSize: 13, color: '#333' },

  // SIDE NAV (EXACT SAME AS TEMPLATE)
  sideNav: { width: 250, backgroundColor: '#000' },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: { width: 38, height: 38, marginRight: 10 },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: { color: '#FDF05D', fontSize: 18, fontWeight: '800' },
  logoDesktopTag: { fontSize: 9, color: '#b3b3b3', letterSpacing: 2, marginTop: 2 },
  sideNavSection: { marginTop: 12, marginBottom: 20, zIndex: 2 },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: { marginRight: 10 },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
  navText: { color: '#f5f5f5', fontSize: 14, fontWeight: '500' },
  lockIcon: { marginLeft: 6 },
  peerMapCard: { borderRadius: 14, overflow: 'hidden', marginTop: 4, marginBottom: 10 },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: { flexDirection: 'row', alignItems: 'center' },
  peerMapTitle: { color: '#f5f5f5', fontSize: 13, fontWeight: '600' },
  peerMapSubtitle: { color: '#b3b3b3', fontSize: 11, marginTop: 2 },
  subscribeCard: { borderRadius: 14, overflow: 'hidden', marginTop: 10 },
  subscribeGradient: { paddingVertical: 10, paddingHorizontal: 12 },
  subscribeContent: { flexDirection: 'row', alignItems: 'center' },
  subscribeTitle: { fontSize: 13, fontWeight: '700', color: '#000' },
  subscribeSubtitle: { fontSize: 11, color: '#1f2933', marginTop: 2 },
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  bottomLinkText: { fontSize: 13, color: '#f5f5f5', fontWeight: '600' },
  bottomLinkCaption: { fontSize: 11, color: '#b3b3b3', marginTop: 2 },

  // CONTENT BANK UI
  galleryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginTop: 2,
    marginBottom: 10,
  },
  galleryTitle: { fontSize: 16, fontWeight: '700', color: '#111' },
  galleryMeta: { fontSize: 12, color: '#777' },

  errorCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
    marginBottom: 12,
  },
  errorRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  errorTitle: { marginLeft: 8, fontSize: 14, fontWeight: '700', color: '#111' },
  errorBody: { fontSize: 12, color: '#555', marginBottom: 10 },
  retryBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#000',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
  },
  retryBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  emptyCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#eee',
    alignItems: 'center',
    marginBottom: 12,
  },
  emptyTitle: { marginTop: 8, fontSize: 15, fontWeight: '800', color: '#111' },
  emptySubtitle: {
    marginTop: 6,
    fontSize: 12,
    color: '#555',
    textAlign: 'center',
    maxWidth: 520,
  },

  masonryRow: { flexDirection: 'row', width: '100%' },
  masonryCol: { flex: 1, paddingHorizontal: 6 },

  mediaCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#eee',
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  mediaTop: { position: 'relative', padding: 10, paddingBottom: 0 },
  mediaImage: { width: '100%', borderRadius: 14, backgroundColor: '#f1f1f1' },
  mediaPlaceholder: {
    position: 'absolute',
    top: 10,
    left: 10,
    right: 10,
    bottom: 10,
    borderRadius: 14,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  typePill: {
    position: 'absolute',
    top: 18,
    left: 18,
    backgroundColor: 'rgba(0,0,0,0.75)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  typePillText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
  },

  nativeVideoFallback: {
    width: '100%',
    borderRadius: 14,
    backgroundColor: '#000',
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeVideoFallbackText: { marginTop: 6, color: '#fff', fontSize: 12, fontWeight: '600' },

  externalCard: {
    width: '100%',
    borderRadius: 14,
    backgroundColor: '#f4f4f4',
    padding: 14,
    borderWidth: 1,
    borderColor: '#e7e7e7',
  },
  externalTopRow: { flexDirection: 'row', alignItems: 'center' },
  externalTitle: { marginLeft: 8, fontSize: 13, fontWeight: '800', color: '#111', flex: 1 },
  externalMid: { marginTop: 14, alignItems: 'center' },
  externalSub: { marginTop: 8, fontSize: 12, color: '#555', fontWeight: '600' },
  externalUrl: { marginTop: 10, fontSize: 11, color: '#777' },

  mediaFooter: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  downloadBtn: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#111',
    flex: 1,
    justifyContent: 'center',
    marginRight: 8,
  },
  downloadBtnText: { marginLeft: 6, fontSize: 12, fontWeight: '800', color: '#000' },

  downloadBtnDisabled: {
    backgroundColor: '#f3f3f3',
    borderColor: '#ddd',
  },
  downloadBtnTextDisabled: {
    color: '#777',
    fontWeight: '700',
  },

  openBtn: {
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    flex: 1,
    justifyContent: 'center',
  },
  openBtnText: { marginLeft: 6, fontSize: 12, fontWeight: '700', color: '#111' },

  loadMoreWrap: { width: '100%', alignItems: 'center', marginTop: 8, marginBottom: 8 },
  loadMoreBtn: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#111',
  },
  loadMoreText: { fontSize: 13, fontWeight: '800', color: '#000', marginLeft: 8, marginRight: 6 },
  endText: { fontSize: 12, color: '#777', marginTop: 6 },
});

export default ContentBank;
